import { useQuery } from "@tanstack/react-query";
import { getRfqInvitation, listRfqInvitations } from "../../../services/rfqService";
import type { RfqBucket, VendorRfqListParams } from "../types";
import { rfqApiError } from "../utils/apiError";
import { rfqKeys } from "./queryKeys";

const MAX_RETRIES = 3;

function statusOf(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const { status, response } = error as { status?: unknown; response?: { status?: unknown } };
  const code = response?.status ?? status;
  return typeof code === "number" ? code : undefined;
}

const isClientError = (error: unknown): boolean => {
  const status = statusOf(error);
  return status !== undefined && status >= 400 && status < 500;
};

const retryUnlessDefinitive = (count: number, error: unknown): boolean => count < MAX_RETRIES && !rfqApiError(error) && !isClientError(error);

export interface RfqListPage {
  bucket: RfqBucket;
  page: number;
  limit: number;
}

function listParams({ bucket, page, limit }: RfqListPage): VendorRfqListParams {
  return { tab: bucket, limit, offset: (page - 1) * limit };
}

export function rfqListQuery(page: RfqListPage) {
  const params = listParams(page);
  return {
    queryKey: rfqKeys.list(params),
    queryFn: () => listRfqInvitations(params),
    keepPreviousData: true,
    retry: retryUnlessDefinitive,
  };
}

export interface RfqDetailQueryOptions {
  refetchInterval?: number | false;
}

export function rfqDetailQuery(id?: string, options?: RfqDetailQueryOptions) {
  return {
    queryKey: rfqKeys.detail(id ?? ""),
    queryFn: () => getRfqInvitation(id ?? ""),
    enabled: Boolean(id),
    refetchInterval: options?.refetchInterval ?? false,
    retry: retryUnlessDefinitive,
  };
}

export function useRfqList(page: RfqListPage) {
  return useQuery(rfqListQuery(page));
}

export function useRfqDetail(id?: string, options?: RfqDetailQueryOptions) {
  return useQuery(rfqDetailQuery(id, options));
}
